import dayjs from 'dayjs';
import { SourceDataV2ViewFragment } from 'generated/graphql';
import {
  FeedbackContentAudioConversation,
  FeedbackContentConversation,
  FeedbackContentForum,
  FeedbackContentReview,
  FeedbackContentSurvey,
} from 'v2/core/entities/feedback/content';
import {
  ClusterLabel,
  EntityLabel,
  L1Label,
  ReasonLabel,
  SearchLabel,
  SentimentLabel,
} from 'v2/core/entities/feedback/labels';
import {
  LanguageFieldValue,
  SentimentName,
  SourceName,
} from 'v2/core/entities/fields';
import { MatchedFeedbackMetadata } from 'v2/core/entities/labels';
import { UUID } from 'v2/core/entities/uuid';

export type FeedbackSourceMetadataValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[];

export interface FeedbackSourceMetadata {
  key: string;
  value: FeedbackSourceMetadataValue;
}

export interface FeedbackSourceData {
  name: SourceName;
  id: string;
  customerID?: string;
  appID?: string;
  country?: string;
  city?: string;
  timezone?: string;
}

export interface Label {
  id: UUID;
  displayName: string;
  createdAt?: dayjs.Dayjs;
  type: FeedbackLabelType;
  isVisible: boolean;
  isUncategorized?: boolean;
  isUpdateInProgress?: boolean;
}

export enum FeedbackContentType {
  Review = 'review',
  Conversation = 'conversation',
  Survey = 'survey',
  Forum = 'forum',
  Audio = 'audio',
}

export interface FeedbackContent {
  type: FeedbackContentType;
  isTranslated: boolean;
}

export type FeedbackMatchType = 'ENTITY' | 'REASON' | 'SEARCH' | undefined;

export interface FeedbackMatch {
  type: FeedbackMatchType;
  matches: MatchedFeedbackMetadata[] | undefined;
}

export enum FeedbackLabelType {
  Sentiment = 'SENTIMENT',
  Reason = 'REASON',
  Cluster = 'CLUSTER',
  Entity = 'ENTITY',
  Search = 'SEARCH',
  L1 = 'L1',
}

export type FeedbackLabelBase =
  | SentimentLabel
  | ReasonLabel
  | ClusterLabel
  | EntityLabel
  | SearchLabel
  | L1Label;

export type FeedbackDetailsConversationVariant = 'preview' | 'default';

export type FeedbackLabel = FeedbackLabelBase & {
  children?: FeedbackLabel[];
  uniquePathKey?: string;
  parentLabel?: FeedbackLabel;
};

export interface Feedback {
  id: UUID;
  enterpretCreatedAt: dayjs.Dayjs;
  sourceCreatedAt?: dayjs.Dayjs;
  language: LanguageFieldValue;
  labels?: FeedbackLabel[];
  content:
    | FeedbackContentSurvey
    | FeedbackContentConversation
    | FeedbackContentReview
    | FeedbackContentForum
    | FeedbackContentAudioConversation;
  sentiment?: SentimentName;
  sourceV2?: SourceDataV2ViewFragment;
  namespace?: string;
  hasUnpredictedRCs?: boolean;
  labelHierarchies?: FeedbackLabel[];
}
